import { ExternalLink, CheckCircle, Brain, Users, Trophy, Flame, Sparkles as SparklesIcon, ListChecks, Shield, ChevronLeft, Mail, FileText } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from "@/components/ui/carousel";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import honeyDoIcon from "@/assets/honey-do-icon.jpeg";
import honeyDoScreenshot1 from "@/assets/honey-do-screenshot-1.jpeg";
import honeyDoScreenshot2 from "@/assets/honey-do-screenshot-2.jpeg";
import honeyDoScreenshot3 from "@/assets/honey-do-screenshot-3.jpeg";
import honeyDoScreenshot4 from "@/assets/honey-do-screenshot-4.jpeg";

const HoneyDo = () => {
  const app = {
    id: "honey-do",
    name: "Honey Do",
    tagline: "ADHD-Focused Family Task Manager",
    description: "A gamified task management app designed specifically for ADHD minds. Honey Do transforms chores and responsibilities into an engaging family experience with points, streaks, leaderboards, and AI-powered productivity features. Perfect for households looking to make task management fun and collaborative.",
    logo: honeyDoIcon,
    url: "https://honey-do-7e04e9a3.base44.app/",
    screenshots: [honeyDoScreenshot1, honeyDoScreenshot2, honeyDoScreenshot3, honeyDoScreenshot4],
    status: "Live",
    features: [
      {
        icon: Trophy,
        title: "Gamification & Rewards",
        description: "Earn points, maintain streaks, compete on leaderboards, and unlock special awards like Top Scorer and Task Master"
      },
      {
        icon: Users,
        title: "Family Collaboration",
        description: "Create a 'Hive' for your household with member profiles, shared tasks, and collaborative goal tracking"
      },
      {
        icon: Brain,
        title: "AI-Powered Productivity",
        description: "AI Task Breakdown, Adaptive Scheduling, and ADHD-Friendly Prioritization that considers cognitive load"
      },
      {
        icon: ListChecks,
        title: "Visual Task Cards",
        description: "Colorful, sticky-note style task cards with drag-to-reorder, due dates, and subtask tracking"
      },
      {
        icon: Flame,
        title: "Streak Motivation",
        description: "Build momentum with daily streaks and 'Race to the Hive' monthly challenges"
      },
      {
        icon: SparklesIcon,
        title: "Smart Organization",
        description: "Filter tasks by Today, Mine, All, or Lists views with pending and completed sections"
      }
    ],
    benefits: [
      "Transforms boring chores into engaging challenges",
      "Reduces task paralysis with visual, bite-sized tasks",
      "Builds healthy habits through streak tracking",
      "Makes accountability fun with family leaderboards",
      "Celebrates progress with special awards and achievements",
      "AI features adapt to your energy and cognitive state"
    ],
    technologies: ["React", "Supabase", "AI Integration", "OAuth", "Gamification Engine", "Responsive Mobile-First Design"]
  };

  const privacyPolicy = [
    {
      id: "info-collected",
      title: "1. Information We Collect",
      content: `We collect only the minimum data necessary to provide HoneyDo's features safely and effectively.

**Parent / Guardian Accounts:**
• Name and email address
• Account login credentials
• Household and family group settings
• Tasks, reminders, notes, and app preferences
• Support communications

**Child Accounts (Under Parent Control):**
• Display name or nickname
• Assigned tasks and app interactions
• Progress, completion status, and in-app activity

Child accounts cannot be created independently and must be approved, managed, and supervised by a parent or legal guardian.

**Automatically Collected Information:**
• Device type and operating system
• App performance and diagnostic data
• Anonymous usage analytics
• Crash logs and error reports

**Information We Do Not Collect:**
• Precise GPS location
• Biometric identifiers
• Health, financial, or government-issued data
• Advertising identifiers for cross-app tracking
• Audio or voice recordings without explicit user action`
    },
    {
      id: "info-usage",
      title: "2. How We Use Your Information",
      content: `We use collected information solely to:

• Provide and operate HoneyDo's core functionality
• Enable family task coordination and parental oversight
• Maintain account security and prevent misuse
• Improve performance, reliability, and user experience
• Communicate important service-related updates

We do not sell personal data and do not use data for targeted advertising.`
    },
    {
      id: "coppa",
      title: "3. Children's Privacy & COPPA Compliance",
      content: `HoneyDo complies with the Children's Online Privacy Protection Act (COPPA).

• Child accounts are accessible only through a verified parent or guardian account
• Parents may review, edit, manage, or delete child data at any time
• We do not knowingly collect personal information from children without parental consent
• If child data is collected without proper authorization, it will be promptly deleted

Parents may contact us to request access to or deletion of a child's information.`
    },
    {
      id: "apple-compliance",
      title: "4. Apple iOS App Store Compliance",
      content: `HoneyDo complies with Apple's:

• App Store Review Guidelines
• App Tracking Transparency (ATT)
• App Privacy Disclosure Requirements

**Key Privacy Commitments:**
• No cross-app or cross-website tracking
• No third-party advertising or profiling
• Clear, purpose-limited permission requests
• Accurate App Store privacy disclosures

HoneyDo does not track users for advertising purposes.`
    },
    {
      id: "data-storage",
      title: "5. Data Storage & Backend",
      content: `HoneyDo uses Base44 as its backend infrastructure.

• Data is stored using secure, industry-standard cloud services
• Access is restricted to authorized systems only
• Infrastructure providers are contractually required to maintain confidentiality and security

While no system can guarantee absolute security, we apply reasonable administrative, technical, and organizational safeguards to protect user data.`
    },
    {
      id: "data-sharing",
      title: "6. Data Sharing & Third Parties",
      content: `We share data only when necessary:

• With essential infrastructure and service providers
• For diagnostics, reliability, and system performance
• To comply with legal obligations
• To protect users, families, or the integrity of the Service

We do not permit third-party advertisers access to user data.`
    },
    {
      id: "data-retention",
      title: "7. Data Retention",
      content: `We retain personal data only as long as necessary to:

• Provide and operate the Service
• Comply with legal and regulatory requirements
• Resolve disputes or enforce agreements

Parents and guardians may request deletion of their account and all associated child data at any time.`
    },
    {
      id: "user-rights",
      title: "8. Your Rights & Controls",
      content: `Depending on your jurisdiction, you may have the right to:

• Access your personal information
• Correct or update inaccurate data
• Request deletion of your data
• Withdraw consent where applicable

Parents retain full control over child account data.

Requests can be submitted using the contact information below.`
    },
    {
      id: "policy-changes",
      title: "9. Changes to This Policy",
      content: `We may update this Privacy Policy periodically. When changes are made:

• The "Last Updated" date will be revised
• Material changes may be communicated through the app or website

Continued use of HoneyDo constitutes acceptance of the updated policy.`
    },
    {
      id: "contact",
      title: "10. Contact Information",
      content: `For privacy-related questions or requests:

**Email:** Voice.vision.media.roc@gmail.com

**Website:** https://voicevisionmedia.ai/portfolio`
    }
  ];

  const termsOfService = [
    {
      id: "acceptance",
      title: "1. Acceptance of Terms",
      content: `By accessing or using HoneyDo ("the App"), you agree to be bound by these Terms of Service. If you do not agree to these terms, please do not use the App.

These terms apply to all users, including parents, guardians, and any child accounts created under parental supervision.`
    },
    {
      id: "description",
      title: "2. Description of Service",
      content: `HoneyDo is a family-oriented task management application designed to help households organize tasks, chores, and responsibilities through gamification features including:

• Task creation and assignment
• Points and rewards systems
• Family leaderboards and streaks
• AI-powered task management features
• Collaborative household management tools`
    },
    {
      id: "accounts",
      title: "3. User Accounts",
      content: `**Account Registration:**
• Users must provide accurate and complete information when creating an account
• You are responsible for maintaining the security of your account credentials
• You must be at least 18 years old to create a parent/guardian account

**Child Accounts:**
• Child accounts can only be created by parents or legal guardians
• Parents/guardians are responsible for all activity on child accounts
• Parents maintain full control and oversight of child account data`
    },
    {
      id: "acceptable-use",
      title: "4. Acceptable Use",
      content: `You agree NOT to:

• Use the App for any unlawful purpose
• Attempt to gain unauthorized access to any part of the App
• Interfere with or disrupt the App's functionality
• Upload harmful content or malware
• Impersonate another person or entity
• Use the App in any way that could harm minors
• Violate any applicable laws or regulations`
    },
    {
      id: "intellectual-property",
      title: "5. Intellectual Property",
      content: `All content, features, and functionality of HoneyDo, including but not limited to text, graphics, logos, icons, images, and software, are the exclusive property of Voice Vision Media and are protected by copyright, trademark, and other intellectual property laws.

You may not copy, modify, distribute, sell, or lease any part of the App without our prior written consent.`
    },
    {
      id: "disclaimers",
      title: "6. Disclaimers & Limitations",
      content: `**Service Availability:**
The App is provided "as is" without warranties of any kind. We do not guarantee uninterrupted or error-free service.

**Limitation of Liability:**
To the fullest extent permitted by law, Voice Vision Media shall not be liable for any indirect, incidental, special, consequential, or punitive damages arising from your use of the App.

**Third-Party Services:**
The App may integrate with third-party services. We are not responsible for the content or practices of these services.`
    },
    {
      id: "termination",
      title: "7. Termination",
      content: `We reserve the right to suspend or terminate your account at any time for:

• Violation of these Terms of Service
• Conduct that we determine is harmful to other users or the App
• Any other reason at our sole discretion

You may also delete your account at any time through the App settings or by contacting us.`
    },
    {
      id: "changes",
      title: "8. Changes to Terms",
      content: `We may modify these Terms of Service at any time. When changes are made:

• The "Last Updated" date will be revised
• Material changes may be communicated through the App
• Continued use of the App constitutes acceptance of the updated terms

We encourage you to review these terms periodically.`
    },
    {
      id: "governing-law",
      title: "9. Governing Law",
      content: `These Terms of Service shall be governed by and construed in accordance with the laws of the State of New York, without regard to its conflict of law provisions.

Any disputes arising from these terms shall be resolved in the courts of New York.`
    },
    {
      id: "tos-contact",
      title: "10. Contact Information",
      content: `For questions about these Terms of Service:

**Email:** Voice.vision.media.roc@gmail.com

**Website:** https://voicevisionmedia.ai/portfolio`
    }
  ];

  return (
    <div className="min-h-screen pt-20">
      {/* Back Navigation */}
      <div className="container mx-auto px-4 md:px-6 pt-4">
        <Button variant="ghost" asChild className="gap-2 text-muted-foreground hover:text-foreground">
          <Link to="/portfolio">
            <ChevronLeft className="w-4 h-4" />
            Back to Portfolio
          </Link>
        </Button>
      </div>

      {/* App Header */}
      <section className="py-8 md:py-12">
        <div className="container mx-auto px-4 md:px-6">
          <Card className="overflow-hidden border-2 border-border hover:border-accent/50 transition-colors">
            <div className="bg-gradient-to-r from-amber-500/10 via-orange-500/10 to-yellow-500/10 p-8 md:p-12">
              <div className="flex flex-col md:flex-row items-start md:items-center gap-6">
                <div className="w-24 h-24 md:w-32 md:h-32 rounded-2xl bg-background shadow-lg flex items-center justify-center overflow-hidden border border-border">
                  <img 
                    src={app.logo} 
                    alt={`${app.name} logo`} 
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h1 className="text-3xl md:text-4xl font-bold text-primary">{app.name}</h1>
                    <Badge className="bg-green-500/10 text-green-600 border-green-500/30">
                      {app.status}
                    </Badge>
                  </div>
                  <p className="text-xl text-accent font-semibold mb-3">{app.tagline}</p>
                  <p className="text-muted-foreground max-w-2xl">{app.description}</p>
                </div>
                <Button asChild size="lg" className="bg-accent hover:bg-accent/90 text-primary font-semibold">
                  <a href={app.url} target="_blank" rel="noopener noreferrer">
                    Visit App
                    <ExternalLink className="w-4 h-4 ml-2" />
                  </a>
                </Button>
              </div>
            </div>

            <CardContent className="p-8 md:p-12">
              {/* Screenshots Gallery */}
              <div className="mb-12">
                <h2 className="text-2xl font-bold text-primary mb-6">App Screenshots</h2>
                <Carousel className="w-full max-w-4xl mx-auto">
                  <CarouselContent>
                    {app.screenshots.map((screenshot, index) => (
                      <CarouselItem key={index} className="md:basis-1/2 lg:basis-1/3">
                        <div className="p-2">
                          <div className="rounded-2xl overflow-hidden border-2 border-border shadow-lg bg-background">
                            <img 
                              src={screenshot} 
                              alt={`${app.name} screenshot ${index + 1}`}
                              className="w-full h-auto object-contain"
                            />
                          </div>
                        </div>
                      </CarouselItem>
                    ))}
                  </CarouselContent>
                  <CarouselPrevious className="left-0 md:-left-12" />
                  <CarouselNext className="right-0 md:-right-12" />
                </Carousel>
              </div>

              {/* Features Grid */}
              <div className="mb-12">
                <h2 className="text-2xl font-bold text-primary mb-6">Key Features</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {app.features.map((feature, index) => (
                    <div 
                      key={index} 
                      className="flex gap-4 p-6 rounded-xl bg-muted/50 hover:bg-muted transition-colors"
                    >
                      <div className="w-12 h-12 rounded-lg bg-accent/20 flex items-center justify-center flex-shrink-0">
                        <feature.icon className="w-6 h-6 text-accent" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-foreground mb-1">{feature.title}</h3>
                        <p className="text-sm text-muted-foreground">{feature.description}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Benefits */}
              <div className="mb-12">
                <h2 className="text-2xl font-bold text-primary mb-6">Benefits</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {app.benefits.map((benefit, index) => (
                    <div key={index} className="flex items-center gap-3">
                      <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                      <span className="text-muted-foreground">{benefit}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Technologies */}
              <div className="mb-12">
                <h2 className="text-2xl font-bold text-primary mb-4">Built With</h2>
                <div className="flex flex-wrap gap-2">
                  {app.technologies.map((tech, index) => (
                    <Badge 
                      key={index} 
                      variant="secondary" 
                      className="bg-primary/10 text-primary border-primary/20"
                    >
                      {tech}
                    </Badge>
                  ))}
                </div>
              </div>

              {/* Contact Section */}
              <div className="mb-12">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-lg bg-accent/20 flex items-center justify-center">
                    <Mail className="w-5 h-5 text-accent" />
                  </div>
                  <h2 className="text-2xl font-bold text-primary">Questions or Feedback?</h2>
                </div>
                <p className="text-muted-foreground mb-4">
                  Have questions about Honey Do or need support? We'd love to hear from you!
                </p>
                <Button asChild variant="outline" className="gap-2">
                  <a href="mailto:Voice.vision.media.roc@gmail.com">
                    <Mail className="w-4 h-4" />
                    Contact Us: Voice.vision.media.roc@gmail.com
                  </a>
                </Button>
              </div>

              {/* Data Privacy Section */}
              <div className="mb-12">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                    <Shield className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-primary">Privacy Policy & Data Usage</h2>
                    <p className="text-sm text-muted-foreground">Effective: January 2026 | Last Updated: January 2026</p>
                  </div>
                </div>
                <p className="text-muted-foreground mb-6">
                  HoneyDo values your privacy and is committed to protecting it. This Privacy Policy explains how we collect, use, store, and protect information when you use the HoneyDo mobile application, website, and related services. HoneyDo is a family-oriented application that allows child accounts only under a parent or legal guardian's supervision and consent.
                </p>
                <Card className="border-border">
                  <CardContent className="p-0">
                    <Accordion type="single" collapsible className="w-full">
                      {privacyPolicy.map((section) => (
                        <AccordionItem key={section.id} value={section.id} className="border-border">
                          <AccordionTrigger className="px-6 py-4 hover:bg-muted/50 text-left">
                            <span className="font-semibold">{section.title}</span>
                          </AccordionTrigger>
                          <AccordionContent className="px-6 pb-4">
                            <div className="prose prose-sm max-w-none text-muted-foreground whitespace-pre-line">
                              {section.content}
                            </div>
                          </AccordionContent>
                        </AccordionItem>
                      ))}
                    </Accordion>
                  </CardContent>
                </Card>
              </div>

              {/* Terms of Service Section */}
              <div>
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 rounded-lg bg-secondary/50 flex items-center justify-center">
                    <FileText className="w-5 h-5 text-foreground" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-primary">Terms of Service</h2>
                    <p className="text-sm text-muted-foreground">Effective: January 2026 | Last Updated: January 2026</p>
                  </div>
                </div>
                <p className="text-muted-foreground mb-6">
                  By using HoneyDo, you agree to be bound by these Terms of Service. Please read them carefully before using the application.
                </p>
                <Card className="border-border">
                  <CardContent className="p-0">
                    <Accordion type="single" collapsible className="w-full">
                      {termsOfService.map((section) => (
                        <AccordionItem key={section.id} value={section.id} className="border-border">
                          <AccordionTrigger className="px-6 py-4 hover:bg-muted/50 text-left">
                            <span className="font-semibold">{section.title}</span>
                          </AccordionTrigger>
                          <AccordionContent className="px-6 pb-4">
                            <div className="prose prose-sm max-w-none text-muted-foreground whitespace-pre-line">
                              {section.content}
                            </div>
                          </AccordionContent>
                        </AccordionItem>
                      ))}
                    </Accordion>
                  </CardContent>
                </Card>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-24 bg-primary">
        <div className="container mx-auto px-4 md:px-6 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-primary-foreground mb-6">
            Have an App Idea?
          </h2>
          <p className="text-xl text-primary-foreground/80 mb-8 max-w-2xl mx-auto">
            We'd love to help bring your vision to life. Schedule a discovery call to discuss your project.
          </p>
          <Button 
            asChild 
            size="lg" 
            className="bg-accent hover:bg-accent/90 text-primary font-semibold"
          >
            <a href="https://calendar.app.google/HfFwVFvYGx42io1B9" target="_blank" rel="noopener noreferrer">
              Schedule a Discovery Call
            </a>
          </Button>
        </div>
      </section>
    </div>
  );
};

export default HoneyDo;
